import React, { FunctionComponent } from "react";
import { formatMessage } from "../../utils/helpers/LanguageHelper";
import { IComboBoxOption } from "@fluentui/react";
import { dsOptionWithDeps, DSOptionWithDeps } from "../generic/dependent-selections/DependentSelectionsEagerModel";
import {
  DSOption,
  dsOptionCollection,
  dsOptionCollectionWithCustomSelector,
} from "../generic/dependent-selections/DependentSelectionsModel";
import DependentSelectionsEagerModelFluentUIView from "../generic/dependent-selections/DependentSelectionsEagerModelFluentUIView";
import { ComponentReference, CoObjectGeneralInfo, CoObjectType } from "../interfaces/CoObject";
import { CoObjectTypeEnum } from "../../api/models";
import { ObjectComponentReferenceSelector } from "./ObjectComponentReferenceSelector";

export interface CoObjectTypeSelectorProps {
  disabled: boolean;
  onCoObjectTypeChange: (value: CoObjectType | undefined) => void; //TODO remove and use only updateCoObjectInfo
  selectedCoObjectType?: string;
  coObjectGeneralInfo: CoObjectGeneralInfo;
  updateWithComponentData: (objectType: CoObjectTypeEnum, componentReference: ComponentReference) => Promise<void>;
}

interface IComboBoxOptionObjectType extends IComboBoxOption {
  enabled: boolean;
}

export const CoObjectTypeSelector: FunctionComponent<CoObjectTypeSelectorProps> = (props) => {
  const { onCoObjectTypeChange, coObjectGeneralInfo, disabled, updateWithComponentData } = props;

  const selectedCoObjectType: CoObjectType | undefined = coObjectGeneralInfo?.objectType;

  const FREE_TEXT_MAIN_TYPE_KEY = "FREE_TEXT_MAIN_TYPE";
  const FREE_TEXT_KEY = "FREE_TEXT";
  const TGK_TEXT_KEY = "TGK_TEXT";
  const COMPONENT_INSTANCE_KEY = "COMPONENT_INSTANCE";

  let options: IComboBoxOptionObjectType[] = [
    { key: "TABLE", text: formatMessage("cof.createnewobject.table"), enabled: true },
    { key: "CHART", text: formatMessage("cof.createnewobject.chart"), enabled: true },
    { key: "CHECKBOX", text: formatMessage("cof.createnewobject.checkbox"), enabled: true },
    { key: "DROPDOWN_LIST", text: formatMessage("cof.createnewobject.dropdown"), enabled: true },
    { key: FREE_TEXT_KEY, text: formatMessage("cof.createnewobject.text"), enabled: true },
    { key: "AMOUNT", text: formatMessage("cof.createnewobject.amount"), enabled: true },
    { key: "DIMENSION_ATTRIBUTE", text: formatMessage("cof.createnewobject.dimensionattribute"), enabled: true },
    { key: "DOCUMENT_PART_INCLUDE", text: formatMessage("cof.createnewobject.documentpart"), enabled: false },
    { key: COMPONENT_INSTANCE_KEY, text: formatMessage("cof.base.componentinstance"), enabled: true },
  ];

  const dsOptions: DSOptionWithDeps[] = options.map((o) => {
    return o.key === FREE_TEXT_KEY
      ? dsOptionWithDeps(
          FREE_TEXT_MAIN_TYPE_KEY,
          formatMessage("cof.createnewobject.text"),
          dsOptionCollection(
            "FREE_TEXT_SUBTYPE",
            "Text Types",
            [
              dsOptionWithDeps(FREE_TEXT_KEY, formatMessage("cof.createnewobject.freetext")),
              dsOptionWithDeps(TGK_TEXT_KEY, formatMessage("cof.createnewobject.cchtagetiktext"), undefined),
            ],
            "radio",
            disabled
          )
        )
      : o.key == COMPONENT_INSTANCE_KEY
      ? dsOptionWithDeps(
          o.key,
          o.text,
          dsOptionCollectionWithCustomSelector("COMPONENT_INSTANCE_SELECTOR", "", (model) => (
            <ObjectComponentReferenceSelector
              dependentSelectionsModelProvider={model}
              onComponentSelected={async (componentSelection) => {
                if (componentSelection) {
                  model.onSelect(provideSelections(componentSelection.type));

                  const componentReference = {
                    componentLogicId: componentSelection.objectLogicId,
                    componentName: componentSelection.objectName,
                    componentDocumentName: componentSelection.documentName,
                  };
                  await updateWithComponentData(componentSelection.type, componentReference);
                } else {
                  model.onSelect(provideSelections(coObjectGeneralInfo?.objectType));
                }
              }}
            ></ObjectComponentReferenceSelector>
          ))
        )
      : dsOptionWithDeps(o.key as string, o.text, undefined, !o.enabled);
  });

  const entryCollection = dsOptionCollection(
    "object_types",
    formatMessage("cof.createnewobject.objecttype"),
    dsOptions,
    "select",
    disabled
  );

  function provideSelections(coObjectType?: CoObjectType): DSOption[] {
    let selections: DSOption[] = [];
    if (coObjectType === FREE_TEXT_KEY || coObjectType === TGK_TEXT_KEY) {
      const mainTextOp = entryCollection.options.find((o) => o.key === FREE_TEXT_MAIN_TYPE_KEY);
      const textOp = mainTextOp.subCollection.options.find((o) => o.key === coObjectType);
      selections = [mainTextOp, textOp];
    } else if (coObjectType != undefined) {
      selections = [entryCollection.options.find((o) => o.key === coObjectType)];
    }
    return selections;
  }

  return (
    <DependentSelectionsEagerModelFluentUIView
      entryCollection={entryCollection}
      onSelect={(selected) =>
        onCoObjectTypeChange(
          selected && selected.length > 0 ? CoObjectTypeEnum[selected[selected.length - 1].key] : undefined
        )
      }
      selections={provideSelections(selectedCoObjectType)}
    />
  );
};
